import { AuthDirectLoginWithPasswordQuery, AuthUserPermission, MpxResponseError } from '@cibo/core'
import { SubmitButton } from '@cibo/ui'
import { Button, Divider, FormHelperText, InputLabel, Stack } from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { AUTH_ROUTES } from '../../pages/consts'
import { Can } from '../Can'

type Props = {
  error?: MpxResponseError
  formAuthToken?: string
  onResendCode?(): void
  onSubmit(values: Omit<AuthDirectLoginWithPasswordQuery, 'formAuthToken' | 'formAuthCode'>): void
  username?: string
  loading: boolean

  formAuthCode?: string
  onForgot(): void
  onEmulate(): void
}

const DirectLoginPasswordForm = ({
  children,
  error,
  formAuthToken,
  formAuthCode,
  loading,
  username,
  onSubmit,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation('auth')

  let errorMessageKey
  switch (error && error.httpCode) {
    case 400:
      errorMessageKey = 'loginError'
      break

    case 500:
    default:
      errorMessageKey = 'serverError'
      break
  }

  const handleSubmitForm = (values: any) => {
    // sending with possibly undefined username to pass our login test.
    // @ts-ignore
    onSubmit(values)
  }

  return (
    <Formik
      initialValues={{
        formAuthCode,
        formAuthToken,
        username: username || '',
        password: '',
      }}
      onSubmit={handleSubmitForm}
      enableReinitialize
    >
      <Form role="main">
        <Stack spacing={2}>
          <Can useAny={AuthUserPermission.CAN_EMULATE}>
            <>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                component={NavLink}
                to={AUTH_ROUTES.EMULATE_USER}
              >
                {t('emulateUser')}
              </Button>
              <Divider />
            </>
          </Can>

          <Stack>
            <InputLabel shrink htmlFor="email">
              {t('email')}
            </InputLabel>
            <Field
              autoFocus
              id="email"
              type="email"
              name="username"
              component={TextField}
              disabled={!formAuthCode && !formAuthToken}
            />
          </Stack>

          <Stack spacing={1}>
            <Stack>
              <InputLabel shrink htmlFor="password">
                {t('password')}
              </InputLabel>
              <Field
                id="password"
                name="password"
                component={TextField}
                autoComplete="current-password"
                type="password"
                disabled={false}
              />
            </Stack>

            <Button
              component={NavLink}
              to={AUTH_ROUTES.FORGOT_PASSWORD}
              sx={{ alignSelf: 'flex-end' }}
            >
              {t('forgotPassword')}
            </Button>
          </Stack>

          {error && <FormHelperText error>{t(errorMessageKey)}</FormHelperText>}

          <SubmitButton
            size="large"
            loading={loading}
            title={t('login')}
            testId={'login_form_button'}
          />

          {children}

          <Stack alignItems="flex-end" spacing={2}>
            <Can useAny={AuthUserPermission.ALLOW_REGISTRATION}>
              <Button component={NavLink} to={AUTH_ROUTES.REGISTRATION}>
                {t('createAccount')}
              </Button>
            </Can>
            <Can useAny={AuthUserPermission.USER_ROSTERED}>
              <Button component={NavLink} to={AUTH_ROUTES.REGISTRATION}>
                {t('rosterEmailRecovery')}
              </Button>
            </Can>
          </Stack>
        </Stack>
      </Form>
    </Formik>
  )
}

export default DirectLoginPasswordForm
